import { XYZ_DEX_NAME } from "#/features/trade/lib/hyperliquid";
import {
	useDeposit,
	usePlaceOrder,
	useUpdateLeverage,
} from "#/hooks/use-trading-actions";
import { useUnifiedBalance } from "#/hooks/use-unified-balance";
import { useWallet } from "#/hooks/use-wallet";
import { toNumber } from "#/lib/formatters";
import {
	useTradeAccount,
	useTradeActiveAssetData,
	useTradeMarket,
	useTradeOrderBook,
} from "../../../hooks/use-trade-data.ts";
import { useTradeSelectedPrice } from "../../trade-selected-price-context.tsx";
import { getMarginCapability } from "../utils/margin-mode.ts";
import { useOrderFormState } from "./use-order-form-state.ts";

export function useOrderFormSetup() {
	const wallet = useWallet();
	const { marketData } = useTradeMarket();
	const { book } = useTradeOrderBook();
	const { accountState, positions } = useTradeAccount();
	const { activeAssetData } = useTradeActiveAssetData();
	const { selectedPrice } = useTradeSelectedPrice();
	const {
		spotUsdc: rawSpotUsdc,
		isUnified,
		isDexAbstraction,
	} = useUnifiedBalance();
	const updateLeverage = useUpdateLeverage();
	const depositAction = useDeposit();
	const placeOrder = usePlaceOrder();
	const currentPosition = positions.find((p) => p.coin === marketData.coin);
	const bestBid = book?.levels[0]?.[0]?.px;
	const bestAsk = book?.levels[1]?.[0]?.px;

	const marginCapability = getMarginCapability({
		coinMarginMode: marketData.marginMode,
	});
	const perpAccountValue = toNumber(
		accountState?.marginSummary?.accountValue ??
			accountState?.crossMarginSummary?.accountValue,
	);

	const effectiveAccountValue = String(rawSpotUsdc || perpAccountValue);
	const effectiveWithdrawable = String(toNumber(accountState?.withdrawable));
	// Spendable equity backing a Cross position. `marginSummary.accountValue` is
	// perp-position MTM only — it's $0 with no open position even when the user is
	// funded — so also consider unified spot USDC and withdrawable, otherwise Cross
	// liq renders "--" before the first trade. (See hooks/ACCOUNT.md.)
	const crossEquity = String(
		Math.max(
			perpAccountValue,
			toNumber(rawSpotUsdc),
			toNumber(accountState?.withdrawable),
		),
	);

	const form = useOrderFormState({
		accountAddress: wallet.address,
		coin: marketData.coin,
		markPrice: marketData.markPrice,
		maxLeverage: marketData.maxLeverage,
		szDecimals: marketData.szDecimals ?? 4,
		withdrawable: effectiveWithdrawable,
		availableToTrade: activeAssetData?.availableToTrade,
		maxTradeSzs: activeAssetData?.maxTradeSzs,
		isXyzMarket: marketData.dex === XYZ_DEX_NAME,
		isUnified,
		isDexAbstraction,
		accountValue: effectiveAccountValue,
		perpAccountValue: crossEquity,
		currentPositionSize: currentPosition?.szi,
		currentPositionEntryPx: currentPosition?.entryPx,
		currentPositionLiquidationPx: currentPosition?.liquidationPx,
		currentPositionLeverage:
			activeAssetData?.leverage ?? currentPosition?.leverage,
		selectedPrice,
		bestBid,
		bestAsk,
		isCrossMarginSupported: marginCapability.supportsCrossMargin,
		defaultMarginMode: marginCapability.defaultMarginMode,
	});

	const quickPrice = form.side === "buy" ? bestBid : bestAsk;

	return {
		wallet,
		marketData,
		form,
		quickPrice,
		marginCapability,
		updateLeverage,
		depositAction,
		placeOrder,
	};
}
